import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  Form,
  Offcanvas,
  FloatingLabel,
  Spinner,
} from "olive-react-myoneui";
import DropdownCommon from "../component/DropdownCommon";
import { toast } from "react-toastify";
import { createQuestion, fetchAllQuestions } from "../../../api/questionApis";
import { getChapters } from "../../../api/chapterApi";
import { getSections } from "../../../api/sectionApi";
import { getSubject } from "../../../api/subjectApis";
import { getSubTopic } from "../../../api/subTopicApis";
import { getTopic } from "../../../api/topicApis";
import { debounce } from "../../../utils/helper";

function QuestionAddOffcanvas({ show, onClose, onAddSuccess }) {
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [existingQuestions, setExistingQuestions] = useState([]);

  const [paper, setPaper] = useState("");
  const [year, setYear] = useState("");
  const [questionNumber, setQuestionNumber] = useState("");
  const [part, setPart] = useState("");
  const [subpart, setSubpart] = useState("");
  const [marks, setMarks] = useState("");
  const [order, setOrder] = useState(1);

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubTopic, setSelectedSubTopic] = useState(null);
  const [selectedType, setSelectedType] = useState("");

  const [questionFile, setQuestionFile] = useState(null);
  const [msFile, setMsFile] = useState(null);
  const [solutionFile, setSolutionFile] = useState(null);
  const [showDependencies, setShowDependencies] = useState(false);
  const [dependencies, setDependencies] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dependencyPage, setDependencyPage] = useState(1);
  const [hasMoreDependencies, setHasMoreDependencies] = useState(true);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [topicSearch, setTopicSearch] = useState("");
  const [subTopicSearch, setSubTopicSearch] = useState("");

  /*new */
  const [chapters, setChapters] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapterSearch, setChapterSearch] = useState("");
  const [sectionSearch, setSectionSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const defaultValue = {
    subject: false,
    topic: false,
    subtopic: false,
    chapter: false,
    section: false,
  };
  const [hasMore, setHasMore] = useState(defaultValue);
  const [dropdDownItemLoading, setDropdDownItemLoading] =
    useState(defaultValue);
  const [page, setPage] = useState({
    subject: 1,
    topic: 1,
    subtopic: 1,
    chapter: 1,
    section: 1,
  });

  const typeList = [{ name: "Question" }, { name: "Context" }];

  const allowedFileTypes = ["application/pdf"];

  const currentYear = new Date().getFullYear();
  const yearList = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => ({
    name: (currentYear - i).toString(),
  }));

  const fetchDependencies = useCallback(async (page) => {
    setLoadingDependencies(true);
    try {
      const questionResp = await fetchAllQuestions(page, 10);
      if (questionResp.data.success) {
        const newQuestions = questionResp.data.data || [];
        setExistingQuestions((prev) =>
          page === 1 ? newQuestions : [...prev, ...newQuestions],
        );
        setHasMoreDependencies(newQuestions.length === 10);
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        fetchData: "Error fetching dependencies",
      }));
    } finally {
      setLoadingDependencies(false);
    }
  }, []);

  const resetForm = () => {
    setPaper("");
    setYear("");
    setQuestionNumber("");
    setPart("");
    setSubpart("");
    setMarks("");
    setOrder(1);
    setSelectedTopic(null);
    setSelectedSubTopic(null);
    setSelectedChapter(null);
    setSelectedSection(null);
    setSelectedType("");
    setQuestionFile(null);
    setMsFile(null);
    setSolutionFile(null);
    setShowDependencies(false);
    setDependencies([]);
    setErrors({});
    setLoading(false);
    setTopicSearch("");
    setSubTopicSearch("");
    setSubTopics([]);
    setChapterSearch("");
    setChapters([]);
    setSectionSearch("");
    setSections([]);
    setDependencyPage(1);
    setHasMoreDependencies(true);
    setExistingQuestions([]);
    setSubjectSearch("");
    setSubjects([]);
    setSelectedSubject(null);
    setPage({
      subject: 1,
      topic: 1,
      subtopic: 1,
      chapter: 1,
      section: 1,
    });
  };

  const trimLeadingSpaces = (text) => text.replace(/^\s+/, "");

  const handleDigitOnlyInput = (value, setter) => {
    let digits = value.replace(/\D/g, "");
    if (digits.length > 4) digits = digits.slice(0, 4);
    setter(digits);
  };

  const handleHierarchyChange = (value, name) => {
    const errorKey = name === "subtopic" ? "subTopic" : name;
    setErrors((prev) => ({ ...prev, [errorKey]: null }));

    if (name === "subject") {
      setSelectedTopic(null);
      setSelectedSubTopic(null);
      setSelectedChapter(null);
      setSelectedSection(null);

      setTopics([]);
      setSubTopics([]);
      setChapters([]);
      setSections([]);
      setTopicSearch("");
      setSubTopicSearch("");
      setChapterSearch("");
      setSectionSearch("");

      setSelectedSubject(value);

      setPage({
        topic: 1,
        subtopic: 1,
        chapter: 1,
        section: 1,
      });
      setHasMore({
        topic: false,
        subtopic: false,
        chapter: false,
        section: false,
      });
    } else if (name === "topic") {
      setSelectedTopic(value);
      setSelectedSubTopic(null);
      setSelectedChapter(null);
      setSelectedSection(null);
      setSubTopics([]);
      setChapters([]);
      setSections([]);
      setSubTopicSearch("");
      setChapterSearch("");
      setSectionSearch("");
      setPage((prev) => ({
        ...prev,
        subtopic: 1,
        chapter: 1,
        section: 1,
      }));
      setHasMore((prev) => ({
        ...prev,
        subtopic: false,
        chapter: false,
        section: false,
      }));
    } else if (name === "subtopic") {
      setSelectedSubTopic(value);
      setSelectedChapter(null);
      setSelectedSection(null);
      setChapters([]);
      setSections([]);
      setChapterSearch("");
      setSectionSearch("");
      setPage((prev) => ({
        ...prev,
        chapter: 1,
        section: 1,
      }));
      setHasMore((prev) => ({
        ...prev,
        chapter: false,
        section: false,
      }));
    } else if (name === "chapter") {
      setSelectedChapter(value);
      setSelectedSection(null);
      setSections([]);
      setSectionSearch("");
      setPage((prev) => ({
        ...prev,
        section: 1,
      }));
      setHasMore((prev) => ({
        ...prev,
        section: false,
      }));
    } else if (name === "section") {
      setSelectedSection(value);
    }
  };

  const handleFileValidation = (file, setterFunction, type) => {
    if (!file) return;

    if (!allowedFileTypes.includes(file.type)) {
      setterFunction(null);
      let fieldName = "Question File";
      if (type === "msFile") fieldName = "Marking Scheme File";
      if (type === "solutionFile") fieldName = "Solution File";

      setErrors((prev) => ({
        ...prev,
        [type]: `${fieldName} must be a PDF file`,
      }));
      return;
    }

    setterFunction(file);
    setErrors((prev) => ({ ...prev, [type]: null }));
  };

  const handleDependencyToggle = (questionId, is_context) => {
    const existing = dependencies.find((d) => d.questionId === questionId);
    if (existing) {
      setDependencies(dependencies.filter((d) => d.questionId !== questionId));
    } else {
      setDependencies([...dependencies, { questionId, is_context }]);
    }
    setErrors((prev) => ({ ...prev, dependencies: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!paper) newErrors.paper = "Paper is required";
    if (!year) newErrors.year = "Year is required";
    if (!questionNumber)
      newErrors.questionNumber = "Question number is required";
    if (!selectedSubject) newErrors.subject = "Subject is required";
    if (!selectedTopic) newErrors.topic = "Topic is required";
    if (!selectedSubTopic) newErrors.subTopic = "Subtopic is required";
    if (!selectedType) newErrors.type = "Type is required";
    if (!questionFile) newErrors.questionFile = "Please upload question file";
    if (selectedSection && !selectedChapter) {
      newErrors.chapter = "Chapter is required when section is selected";
    }
    if (showDependencies && dependencies.length === 0) {
      newErrors.dependencies = "Please select at least one dependency";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setErrors({});

      const formData = new FormData();
      formData.append("paper", paper);
      formData.append("year", year);
      formData.append("question_number", questionNumber);
      formData.append("part", part);
      formData.append("subpart", subpart);
      formData.append("subject", selectedSubject?._id);
      formData.append(
        "section",
        selectedSection ? JSON.stringify([selectedSection._id]) : "[]",
      );
      formData.append(
        "chapter",
        selectedChapter ? JSON.stringify([selectedChapter._id]) : "[]",
      );
      formData.append("marks", selectedType === "Question" ? Number(marks) : 0);
      formData.append("order", order);
      formData.append("topics", JSON.stringify([selectedTopic._id]));
      formData.append("subtopics", JSON.stringify([selectedSubTopic._id]));
      formData.append("question_file", questionFile);
      if (msFile) formData.append("ms_file", msFile);
      if (solutionFile) formData.append("solution_file", solutionFile);
      formData.append("dependencies", JSON.stringify(dependencies));

      const response = await createQuestion(formData);

      if (response.data.success) {
        toast.success(response.data.message || "Question added successfully.");
        onAddSuccess?.();
        onClose();
        resetForm();
      } else {
        const fieldErrors = {};
        response.data.errors?.forEach((err) => {
          fieldErrors[err.param] = err.msg;
        });
        setErrors(fieldErrors);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
      const fieldErrors = {};
      err.response?.data?.errors?.forEach((error) => {
        fieldErrors[error.param] = error.msg;
      });
      setErrors(fieldErrors);
    } finally {
      setLoading(false);
    }
  };

  const getLabelClass = (field) => (errors[field] ? "text-danger" : "");

  // const filteredTopics = topics.filter((t) =>
  //   t.title.toLowerCase().includes(topicSearch.toLowerCase()),
  // );

  const handleDependencyScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 5 &&
      hasMoreDependencies &&
      !loadingDependencies
    ) {
      const nextPage = dependencyPage + 1;
      setDependencyPage(nextPage);
      fetchDependencies(nextPage);
    }
  };

  const fetchSubjects = useCallback(async (pageNumber, search = "") => {
    setDropdDownItemLoading((prev) => ({ ...prev, subject: true }));
    try {
      const response = await getSubject({ title: search, page: pageNumber });
      if (response.data?.success) {
        const newData = (response.data?.data || []).map((s) => ({
          _id: s._id,
          name: s.title,
        }));
        setSubjects((prev) =>
          pageNumber === 1 ? newData : [...prev, ...newData],
        );
        setHasMore((prev) => ({
          ...prev,
          subject: newData.length === 10, // Assuming limit is 10
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDropdDownItemLoading((prev) => ({ ...prev, subject: false }));
    }
  }, []);

  const fetchTopics = useCallback(
    async (pageNumber, search = "", subjectId = selectedSubject?._id) => {
      if (!subjectId) return;
      setDropdDownItemLoading((prev) => ({ ...prev, topic: true }));
      try {
        const response = await getTopic({
          subject: subjectId,
          title: search,
          page: pageNumber,
        });
        if (response.data?.success) {
          const newData = (response.data?.data || []).map((t) => ({
            _id: t._id,
            name: t.title,
          }));
          setTopics((prev) =>
            pageNumber === 1 ? newData : [...prev, ...newData],
          );
          setHasMore((prev) => ({
            ...prev,
            topic: newData.length === 10,
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDropdDownItemLoading((prev) => ({ ...prev, topic: false }));
      }
    },
    [selectedSubject],
  );

  const fetchSubTopics = useCallback(
    async (pageNumber, search = "", topicId = selectedTopic?._id) => {
      if (!topicId) return;
      setDropdDownItemLoading((prev) => ({ ...prev, subtopic: true }));
      try {
        const response = await getSubTopic({
          topic: topicId,
          title: search,
          page: pageNumber,
        });
        if (response.data?.success) {
          const newData = (response.data?.data || []).map((s) => ({
            _id: s._id,
            name: s.title,
          }));
          setSubTopics((prev) =>
            pageNumber === 1 ? newData : [...prev, ...newData],
          );
          setHasMore((prev) => ({
            ...prev,
            subtopic: newData.length === 10,
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDropdDownItemLoading((prev) => ({ ...prev, subtopic: false }));
      }
    },
    [selectedTopic],
  );

  const fetchChapters = useCallback(
    async (pageNumber, search = "", subTopicId = selectedSubTopic?._id) => {
      if (!subTopicId) return;
      setDropdDownItemLoading((prev) => ({ ...prev, chapter: true }));
      try {
        const response = await getChapters({
          subTopic: subTopicId,
          search: search,
          page: pageNumber,
        });
        if (response?.success) {
          const newData = (response.data || []).map((c) => ({
            _id: c._id,
            name: c.title,
          }));
          setChapters((prev) =>
            pageNumber === 1 ? newData : [...prev, ...newData],
          );
          setHasMore((prev) => ({
            ...prev,
            chapter: newData.length === 20, // Assuming limit is 20
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDropdDownItemLoading((prev) => ({ ...prev, chapter: false }));
      }
    },
    [selectedSubTopic],
  );

  const fetchSections = useCallback(
    async (pageNumber, search = "", chapterId = selectedChapter?._id) => {
      if (!chapterId) return;
      setDropdDownItemLoading((prev) => ({ ...prev, section: true }));
      try {
        const response = await getSections({
          chapter: chapterId,
          search: search,
          page: pageNumber,
        });
        if (response?.success) {
          const newData = (response.data || []).map((s) => ({
            _id: s._id,
            name: s.title,
          }));
          setSections((prev) =>
            pageNumber === 1 ? newData : [...prev, ...newData],
          );
          setHasMore((prev) => ({
            ...prev,
            section: newData.length === 20, // Assuming limit is 20
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDropdDownItemLoading((prev) => ({ ...prev, section: false }));
      }
    },
    [selectedChapter],
  );

  const debouncedFetchSubjects = useCallback(
    debounce((page, search) => fetchSubjects(page, search), 500),
    [fetchSubjects],
  );

  const debouncedFetchTopics = useCallback(
    debounce(
      (page, search, subjectId) => fetchTopics(page, search, subjectId),
      500,
    ),
    [fetchTopics],
  );

  const debouncedFetchSubTopics = useCallback(
    debounce(
      (page, search, topicId) => fetchSubTopics(page, search, topicId),
      500,
    ),
    [fetchSubTopics],
  );

  const debouncedFetchChapters = useCallback(
    debounce(
      (page, search, subTopicId) => fetchChapters(page, search, subTopicId),
      500,
    ),
    [fetchChapters],
  );

  const debouncedFetchSections = useCallback(
    debounce(
      (page, search, chapterId) => fetchSections(page, search, chapterId),
      500,
    ),
    [fetchSections],
  );

  useEffect(() => {
    if (!show) return;
    debouncedFetchSubjects(1, subjectSearch);
  }, [show, subjectSearch, debouncedFetchSubjects]);

  useEffect(() => {
    if (!show || !selectedSubject) return;
    debouncedFetchTopics(1, topicSearch, selectedSubject._id);
  }, [topicSearch, selectedSubject, debouncedFetchTopics]);

  useEffect(() => {
    if (!show || !selectedTopic) return;
    debouncedFetchSubTopics(1, subTopicSearch, selectedTopic._id);
  }, [subTopicSearch, selectedTopic, debouncedFetchSubTopics]);

  useEffect(() => {
    if (!show || !selectedSubTopic) return;
    debouncedFetchChapters(1, chapterSearch, selectedSubTopic._id);
  }, [chapterSearch, selectedSubTopic, debouncedFetchChapters]);

  useEffect(() => {
    if (!show || !selectedChapter) return;
    debouncedFetchSections(1, sectionSearch, selectedChapter._id);
  }, [sectionSearch, selectedChapter, debouncedFetchSections]);

  const handleLoadMore = (type) => {
    if (!type || dropdDownItemLoading[type] || !hasMore[type]) return;

    setPage((prev) => {
      const nextPage = prev[type] + 1;

      switch (type) {
        case "subject":
          fetchSubjects(nextPage, subjectSearch);
          break;

        case "topic":
          fetchTopics(nextPage, topicSearch, selectedSubject?._id);
          break;

        case "subtopic":
          fetchSubTopics(nextPage, subTopicSearch, selectedTopic?._id);
          break;

        case "chapter":
          fetchChapters(nextPage, chapterSearch, selectedSubTopic?._id);
          break;

        case "section":
          fetchSections(nextPage, sectionSearch, selectedChapter?._id);
          break;

        default:
          break;
      }

      return {
        ...prev,
        [type]: nextPage,
      };
    });
  };

  useEffect(() => {
    if (!show) return;
    fetchDependencies(1);
  }, [show, fetchDependencies]);

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        onClose();
        resetForm();
      }}
      placement="end"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Add Question</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {/* PAPER */}
        <FloatingLabel
          controlId="paperId"
          label={<span className={getLabelClass("paper")}>Paper *</span>}
          className="mb-3"
        >
          <Form.Control
            type="text"
            placeholder="Enter Paper"
            value={paper}
            onChange={(e) => {
              setPaper(trimLeadingSpaces(e.target.value));
              setErrors((prev) => ({ ...prev, paper: null }));
            }}
          />
          {errors.paper && (
            <div className="text-danger mt-1">{errors.paper}</div>
          )}
        </FloatingLabel>

        {/* YEAR */}
        <Form.Group className="mb-3">
          <DropdownCommon
            selectedValue={year ? { name: year } : null}
            setSelectedValue={(y) => {
              setYear(y.name);
              setErrors((prev) => ({ ...prev, year: null }));
            }}
            items={yearList}
            label={<span className={getLabelClass("year")}>Year *</span>}
            searchable={false}
          />
          {errors.year && <div className="text-danger mt-1">{errors.year}</div>}
        </Form.Group>

        {/* QUESTION FILE */}
        <Form.Group className="mb-3">
          <Form.Label className={getLabelClass("questionFile")}>
            Question File *
          </Form.Label>
          <Form.Control
            type="file"
            accept=".pdf"
            onChange={(e) =>
              handleFileValidation(
                e.target.files[0],
                setQuestionFile,
                "questionFile",
              )
            }
          />
          {errors.questionFile && (
            <div className="text-danger mt-1">{errors.questionFile}</div>
          )}
        </Form.Group>

        {/* MS FILE */}
        <Form.Group className="mb-3">
          <Form.Label>Marking Scheme File (Optional)</Form.Label>
          <Form.Control
            type="file"
            accept=".pdf"
            onChange={(e) =>
              handleFileValidation(e.target.files[0], setMsFile, "msFile")
            }
          />
        </Form.Group>
        {/* SOLUTION FILE */}
        <Form.Group className="mb-3">
          <Form.Label>Solution File (Optional)</Form.Label>
          <Form.Control
            type="file"
            accept=".pdf"
            onChange={(e) =>
              handleFileValidation(
                e.target.files[0],
                setSolutionFile,
                "solutionFile",
              )
            }
          />
          {errors.solutionFile && (
            <div className="text-danger mt-1">{errors.solutionFile}</div>
          )}
        </Form.Group>

        {/* QUESTION NUMBER */}
        <FloatingLabel
          controlId="questionNumberId"
          label={
            <span className={getLabelClass("questionNumber")}>
              Question Number *
            </span>
          }
          className="mb-3"
        >
          <Form.Control
            type="text"
            placeholder="Enter Question Number"
            value={questionNumber}
            onChange={(e) => {
              handleDigitOnlyInput(e.target.value, setQuestionNumber);
              setErrors((prev) => ({ ...prev, questionNumber: null }));
            }}
          />
          {errors.questionNumber && (
            <div className="text-danger mt-1">{errors.questionNumber}</div>
          )}
        </FloatingLabel>

        {/* PART */}
        <FloatingLabel controlId="partId" label="Part" className="mb-3">
          <Form.Control
            type="text"
            placeholder="Enter Part"
            value={part}
            onChange={(e) => setPart(trimLeadingSpaces(e.target.value))}
          />
        </FloatingLabel>

        {/* SUBPART */}
        <FloatingLabel controlId="subpartId" label="Subpart" className="mb-3">
          <Form.Control
            type="text"
            placeholder="Enter Subpart"
            value={subpart}
            onChange={(e) => setSubpart(trimLeadingSpaces(e.target.value))}
          />
        </FloatingLabel>

        {/* ORDER */}
        <FloatingLabel controlId="orderId" label="Order" className="mb-3">
          <Form.Control
            type="number"
            placeholder="Enter Order"
            value={order}
            onChange={(e) => handleDigitOnlyInput(e.target.value, setOrder)}
          />
        </FloatingLabel>

        {/* SUBJECT DROPDOWN */}
        <Form.Group className="mb-3">
          <DropdownCommon
            name="subject"
            selectedValue={selectedSubject}
            setSelectedValue={(val) => handleHierarchyChange(val, "subject")}
            items={subjects}
            label={<span className={getLabelClass("subject")}>Subject *</span>}
            searchable={true}
            remoteSearch={true}
            searchValue={subjectSearch}
            setSearchValue={(val) => {
              setSubjectSearch(val);
              setPage((prev) => ({ ...prev, subject: 1 }));
            }}
            hasMore={hasMore.subject}
            onLoadMore={() => handleLoadMore("subject")}
            isLoading={dropdDownItemLoading.subject}
          />
          {errors?.subject && (
            <div className="text-danger mt-1">{errors.subject}</div>
          )}
        </Form.Group>

        {/* TOPIC DROPDOWN */}
        <Form.Group className="mb-3">
          <DropdownCommon
            name="topic"
            key={`topic-${selectedSubject?._id || "none"}`}
            selectedValue={selectedTopic}
            setSelectedValue={(val) => handleHierarchyChange(val, "topic")}
            items={topics}
            label={<span className={getLabelClass("topic")}>Topic *</span>}
            searchable={true}
            remoteSearch={true}
            searchValue={topicSearch}
            setSearchValue={(val) => {
              setTopicSearch(val);
              setPage((prev) => ({ ...prev, topic: 1 }));
            }}
            hasMore={hasMore.topic}
            onLoadMore={() => handleLoadMore("topic")}
            isLoading={dropdDownItemLoading.topic}
          />
          {errors.topic && (
            <div className="text-danger mt-1">{errors.topic}</div>
          )}
        </Form.Group>

        {/* SUBTOPIC DROPDOWN */}
        <Form.Group className="mb-3">
          <DropdownCommon
            name="subtopic"
            key={`subtopic-${selectedSubject?._id || "none"}-${selectedTopic?._id || "none"}`}
            selectedValue={selectedSubTopic}
            setSelectedValue={(val) => handleHierarchyChange(val, "subtopic")}
            items={subTopics}
            label={
              <span className={getLabelClass("subTopic")}>Sub Topic *</span>
            }
            searchable={true}
            remoteSearch={true}
            searchValue={subTopicSearch}
            setSearchValue={(val) => {
              setSubTopicSearch(val);
              setPage((prev) => ({ ...prev, subtopic: 1 }));
            }}
            hasMore={hasMore.subtopic}
            onLoadMore={() => handleLoadMore("subtopic")}
            isLoading={dropdDownItemLoading.subtopic}
          />
          {errors.subTopic && (
            <div className="text-danger mt-1">{errors.subTopic}</div>
          )}
        </Form.Group>

        {/* CHAPTER DROPDOWN */}
        <Form.Group className="mb-3">
          <DropdownCommon
            name="chapter"
            key={`chapter-${selectedSubject?._id || "none"}-${selectedTopic?._id || "none"}-${selectedSubTopic?._id || "none"}`}
            selectedValue={selectedChapter}
            setSelectedValue={(val) => handleHierarchyChange(val, "chapter")}
            items={chapters}
            label={
              <span className={getLabelClass("chapter")}>
                Chapter (Optional)
              </span>
            }
            searchable={true}
            remoteSearch={true}
            searchValue={chapterSearch}
            setSearchValue={(val) => {
              setChapterSearch(val);
              setPage((prev) => ({ ...prev, chapter: 1 }));
            }}
            hasMore={hasMore.chapter}
            onLoadMore={() => handleLoadMore("chapter")}
            isLoading={dropdDownItemLoading.chapter}
          />
          {errors.chapter && (
            <div className="text-danger mt-1">{errors.chapter}</div>
          )}
        </Form.Group>

        {/* SECTION */}
        <Form.Group className="mb-3">
          <DropdownCommon
            name="section"
            key={`section-${selectedSubject?._id || "none"}-${selectedTopic?._id || "none"}-${selectedSubTopic?._id || "none"}-${selectedChapter?._id || "none"}`}
            selectedValue={selectedSection}
            setSelectedValue={(val) => handleHierarchyChange(val, "section")}
            items={sections}
            label={
              <span className={getLabelClass("section")}>
                Section (Optional)
              </span>
            }
            searchable={true}
            remoteSearch={true}
            searchValue={sectionSearch}
            setSearchValue={(val) => {
              setSectionSearch(val);
              setPage((prev) => ({ ...prev, section: 1 }));
            }}
            hasMore={hasMore.section}
            onLoadMore={() => handleLoadMore("section")}
            isLoading={dropdDownItemLoading.section}
          />
          {errors.section && (
            <div className="text-danger mt-1">{errors.section}</div>
          )}
        </Form.Group>

        {/* TYPE */}
        <Form.Group className="mb-3">
          <DropdownCommon
            selectedValue={selectedType ? { name: selectedType } : null}
            setSelectedValue={(t) => {
              setSelectedType(t.name);
              setErrors((prev) => ({ ...prev, type: null }));
            }}
            items={typeList}
            label={<span className={getLabelClass("type")}>Type *</span>}
          />
          {errors.type && <div className="text-danger mt-1">{errors.type}</div>}
        </Form.Group>

        {selectedType === "Question" && (
          <FloatingLabel controlId="marksId" label="Marks" className="mb-3">
            <Form.Control
              type="number"
              placeholder="Enter Marks"
              value={marks}
              onChange={(e) => handleDigitOnlyInput(e.target.value, setMarks)}
            />
          </FloatingLabel>
        )}

        {/* DEPENDENCIES */}
        <Form.Check
          className="mb-3"
          type="checkbox"
          label="Has Dependencies?"
          checked={showDependencies}
          onChange={(e) => setShowDependencies(e.target.checked)}
        />

        {showDependencies && (
          <>
            <Card
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                border: errors.dependencies ? "1px solid red" : "",
              }}
              onScroll={handleDependencyScroll}
            >
              <Card.Body>
                {existingQuestions.map((q) => (
                  <Form.Check key={q._id} className="mb-2">
                    <Form.Check.Input
                      type="checkbox"
                      id={q._id}
                      checked={dependencies.some(
                        (d) => d.questionId?.toString() === q._id?.toString(),
                      )}
                      onChange={() => handleDependencyToggle(q._id, true)}
                    />
                    <Form.Check.Label htmlFor={q._id}>
                      {q.paper} - {q.question_number}
                    </Form.Check.Label>
                  </Form.Check>
                ))}
                {loadingDependencies && (
                  <div className="text-center">
                    <Spinner size="sm" />
                  </div>
                )}
              </Card.Body>
            </Card>

            {errors.dependencies && (
              <div className="text-danger mt-1">{errors.dependencies}</div>
            )}
          </>
        )}
      </Offcanvas.Body>

      <Offcanvas.Footer>
        <Button
          variant="default"
          onClick={() => {
            onClose();
            resetForm();
          }}
        >
          Cancel
        </Button>

        <Button variant="primary" onClick={onSave} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Offcanvas.Footer>
    </Offcanvas>
  );
}

export default QuestionAddOffcanvas;
